import React, { useCallback, useRef } from 'react';
import useMountEffect from '../../hooks/useMountEffect';

type PriorityCard = {
	woPriorityId: number;
	woPriorityName: string;
	completedWoCount: number;
	color: string;
	bgColor: string;
};

type PriorityCardScrollerProps = {
	cards: PriorityCard[];
	selectedPriorityId: number | null;
	onCardClick: (priorityId: number) => void;
};

const PriorityCardScroller = ({
	cards,
	selectedPriorityId,
	onCardClick,
}: PriorityCardScrollerProps) => {
	const scrollerRef = useRef<HTMLDivElement>(null);

	useMountEffect(() => {
		const node = scrollerRef.current;
		if (!node) return;

		const handleWheel = (event: WheelEvent) => {
			if (node.scrollWidth <= node.clientWidth) return;
			const verticalDelta = event.deltaY;
			const horizontalDelta = event.deltaX;
			if (verticalDelta === 0 && horizontalDelta === 0) return;
			event.preventDefault();
			node.scrollLeft += verticalDelta !== 0 ? verticalDelta : horizontalDelta;
		};

		node.addEventListener('wheel', handleWheel, { passive: false });
		return () => {
			node.removeEventListener('wheel', handleWheel);
		};
	});

	const handleCardClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			const raw = event.currentTarget.dataset.priorityId || '';
			const priorityId = parseInt(raw, 10);
			if (Number.isNaN(priorityId)) return;
			onCardClick(priorityId);
		},
		[onCardClick]
	);

	return (
		<div
			ref={scrollerRef}
			className="mt-3 flex gap-s overflow-x-auto py-px [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
		>
			{cards.map((card) => {
				const isSelected = card.woPriorityId === selectedPriorityId;
				return (
					<button
						key={card.woPriorityId}
						type="button"
						data-priority-id={card.woPriorityId}
						aria-pressed={isSelected}
						onClick={handleCardClick}
						className={`flex h-[82px] w-[226.8px] flex-shrink-0 cursor-pointer flex-col items-start gap-xs rounded-[4px] border border-solid border-grey-5 p-s text-left transition-colors ${
							isSelected ? 'bg-purple-6' : 'bg-grey-1'
						}`}
					>
						<span
							className={`bodymedium inline-block max-w-full truncate px-3 py-0.5 ${
								isSelected ? 'text-white' : 'rounded-[24px]'
							}`}
							style={
								isSelected
									? undefined
									: {
											color: card.color,
											backgroundColor: card.bgColor,
									  }
							}
						>
							{card.woPriorityName}
						</span>
						<div className="px-3">
							<span className={`h5medium ${isSelected ? 'text-white' : 'text-character-primary'}`}>
								{card.completedWoCount}
							</span>{' '}
							<span
								className={`bodyregular ${
									!isSelected ? 'text-character-secondary' : 'text-grey-1'
								}`}
							>
								WOs
							</span>
						</div>
					</button>
				);
			})}
		</div>
	);
};

export default PriorityCardScroller;
